import { useRef, useEffect, useCallback } from "react";

/**
 * Returns a debounced function and a cancel function. On unmount, pending
 * invocations are cleared.
 * @param {(...args: any[]) => void} callback
 * @param {number} delayMs
 * @returns {[function, function]} [debounced, cancel]
 */
export function useDebouncedCallback(callback, delayMs) {
  const idRef = useRef(null);
  const cbRef = useRef(callback);
  cbRef.current = callback;

  const cancel = useCallback(() => {
    if (idRef.current) {
      clearTimeout(idRef.current);
      idRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args) => {
      if (idRef.current) {
        clearTimeout(idRef.current);
      }
      idRef.current = setTimeout(() => {
        idRef.current = null;
        cbRef.current(...args);
      }, delayMs);
    },
    [delayMs]
  );

  useEffect(() => () => cancel(), [cancel]);

  return [debounced, cancel];
}
